import React, { useState, useEffect } from 'react'
import styles from './LanguageModal.module.scss'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import Select from 'react-select'

const LanguageModal = ({ isModalOpen, onFinish, onReturn, initialLanguages }) => {
	const [isAlertMessageVisible, setIsAlertMessageVisible] = useState(false)
	const [languages, setLanguages] = useState(() => {
		if (initialLanguages && initialLanguages.length > 0) {
			return initialLanguages.map((language, index) => ({
				...language,
				id:
					language.id && typeof language.id === 'number' && !isNaN(language.id)
						? language.id
						: index + 1,
			}))
		}
		return [{ id: 1, language_name: '', proficiency: '', custom_language: '' }]
	})

	useEffect(() => {
		let timer

		if (isAlertMessageVisible) {
			timer = setTimeout(() => {
				setIsAlertMessageVisible(false)
			}, 3000)
		}

		return () => {
			clearTimeout(timer)
		}
	}, [isAlertMessageVisible])

	if (isModalOpen === false) {
		return null
	}

	const handleLanguageChange = (id, field, value) => {
		setLanguages((prevLanguages) =>
			prevLanguages.map((language) =>
				language.id === id ? { ...language, [field]: value } : language,
			),
		)
	}

	const handleAddLanguage = () => {
		// ID 생성 로직 개선
		let newId = 1
		if (languages.length > 0) {
			const existingIds = languages
				.map((l) => l.id)
				.filter((id) => typeof id === 'number' && !isNaN(id))
			if (existingIds.length > 0) {
				newId = Math.max(...existingIds) + 1
			}
		}
		setLanguages((prevLanguages) => [
			...prevLanguages,
			{ id: newId, language_name: '', proficiency: '', custom_language: '' },
		])
	}

	const handleDeleteLanguage = (id) => {
		if (languages.length > 1) {
			setLanguages((prevLanguages) => prevLanguages.filter((language) => language.id !== id))
		}
	}

	const validateLanguage = (language) => {
		const errors = []

		// 1. 언어명 선택 검사
		if (!language.language_name) {
			errors.push('언어를 선택해주세요')
		}

		// 2. 기타 언어 선택 시 직접 입력 검사
		if (
			language.language_name === 'OTHER' &&
			(!language.custom_language || language.custom_language.trim() === '')
		) {
			errors.push('언어명을 직접 입력해주세요')
		}

		// 3. 수준 선택 검사
		if (!language.proficiency) {
			errors.push('수준을 선택해주세요')
		}

		return errors
	}

	const isFormValid = () => {
		// 언어가 1개도 차있지 않으면 false
		if (languages.length === 0) return false

		// 모든 언어가 유효한지 검사
		return languages.every((language) => {
			const errors = validateLanguage(language)
			return errors.length === 0
		})
	}

	const handleFinishClick = () => {
		// 전체 유효성 검사
		const allErrors = []
		languages.forEach((language, index) => {
			const errors = validateLanguage(language)
			if (errors.length > 0) {
				allErrors.push(`언어 ${index + 1}: ${errors.join(', ')}`)
			}
		})

		if (allErrors.length > 0) {
			setIsAlertMessageVisible(true)
			console.log('유효성 검사 실패:', allErrors)
			return
		}

		onFinish(languages)
	}

	const handleReturn = () => {
		onReturn()
	}

	const proficiencyOptions = [
		{ value: 'BEGINNER', label: '초급' },
		{ value: 'INTERMEDIATE', label: '중급' },
		{ value: 'ADVANCED', label: '고급' },
	]

	const commonLanguages = [
		{ value: '영어', label: '영어' },
		{ value: '일본어', label: '일본어' },
		{ value: 'OTHER', label: '기타' },
	]

	return (
		<div className={styles.modalOverlay}>
			<div className={styles.modalContainer}>
				<div className={styles.modalHeader}>
					<h3>언어 능력</h3>
					<p>보유하고 있는 언어 능력을 입력해주세요</p>
				</div>

				<div className={styles.languageList}>
					{languages.map((language, index) => (
						<div key={language.id} className={styles.languageItem}>
							<div className={styles.languageHeader}>
								<span className={styles.languageNumber}>언어 {index + 1}</span>

								{index > 0 && (
									<button
										type="button"
										className={styles.deleteButton}
										onClick={() => handleDeleteLanguage(language.id)}
										disabled={languages.length === 1}>
										<TrashIcon className={styles.trashIcon} />
									</button>
								)}
							</div>

							<div className={styles.inputGroup}>
								<div className={styles.inputField}>
									<label>언어</label>
									<Select
										value={
											commonLanguages.find(
												(option) => option.value === language.language_name,
											) || null
										}
										onChange={(selectedOption) =>
											handleLanguageChange(
												language.id,
												'language_name',
												selectedOption?.value || '',
											)
										}
										options={commonLanguages}
										placeholder="언어 선택"
										isSearchable={false}
										classNamePrefix="custom-select"
									/>
									{language.language_name === 'OTHER' && (
										<div className={styles.customLanguageField}>
											<input
												type="text"
												placeholder="직접 입력"
												value={language.custom_language || ''}
												onChange={(e) =>
													handleLanguageChange(
														language.id,
														'custom_language',
														e.target.value,
													)
												}
												className={styles.customLanguageInput}
											/>
										</div>
									)}
								</div>

								<div className={styles.inputField}>
									<label>수준</label>
									<Select
										value={
											proficiencyOptions.find(
												(option) => option.value === language.proficiency,
											) || null
										}
										onChange={(selectedOption) =>
											handleLanguageChange(
												language.id,
												'proficiency',
												selectedOption?.value || '',
											)
										}
										options={proficiencyOptions}
										placeholder="수준 선택"
										isSearchable={false}
										classNamePrefix="custom-select"
									/>
								</div>
							</div>
						</div>
					))}
				</div>

				<div className={styles.addButton} onClick={handleAddLanguage}>
					<PlusIcon className={styles.plusIcon} />
					<span>언어 추가하기</span>
				</div>

				<div className={styles.modalFooter}>
					<button className={styles.cancelButton} onClick={handleReturn} type="button">
						취소
					</button>
					<button
						className={styles.finishButton}
						onClick={handleFinishClick}
						type="button"
						disabled={!isFormValid()}>
						완료
					</button>
				</div>
			</div>

			{isAlertMessageVisible && (
				<div className={styles.alertMessageBox}>
					<ExclamationCircleIcon className={styles.warningIcon} />
					<p>필수 항목이 누락되었어요.</p>
					<p>다시 입력해주세요.</p>
				</div>
			)}
		</div>
	)
}

export default LanguageModal
